import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import { FaArrowLeft, FaChartLine, FaTrophy, FaUsers, FaCalendarAlt, FaExclamationTriangle, FaSort, FaSortUp, FaSortDown, FaPrint } from 'react-icons/fa';
import { getSportConfig } from '../../config/sportStats';
import './SeasonReport.css';
import API_BASE_URL from '../../config/api';

const SeasonReport = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/teams/${teamId}/report`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setReport(data);
        } else {
          throw new Error('Falha ao carregar o relatório da época.');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [teamId]);

  const sportConfig = useMemo(() => {
    if (!report) return null;
    return getSportConfig(report.sportName);
  }, [report]);

  const sortedAthleteStats = useMemo(() => {
    if (!report || !report.athleteStats) return [];

    let sortableItems = [...report.athleteStats];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'name') {
          aValue = a.name;
          bValue = b.name;
        } else if (sortConfig.key === 'gamesPlayed') {
          aValue = a.gamesPlayed;
          bValue = b.gamesPlayed;
        } else {
          aValue = a.averages[sortConfig.key] || 0;
          bValue = b.averages[sortConfig.key] || 0;
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [report, sortConfig]);

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="sort-icon-inactive" />;
    return sortConfig.direction === 'asc' ? <FaSortUp className="sort-icon-active" /> : <FaSortDown className="sort-icon-active" />;
  };

  if (loading) return <div className="report-loading">A gerar relatório…</div>;
  if (error) return <div className="report-error">Erro: {error}</div>;
  if (!report) return null;

  const { performance, attendance, athleteStats, sportName } = report;

  const isSetBased = sportConfig?.type === 'sets';
  const scoreLabel = isSetBased ? "Sets" : (sportName === "Basquetebol" ? "Pontos" : "Golos");

  const COLORS = ['#10b981', '#ef4444', '#f59e0b'];
  const pieData = [
    { name: 'Vitórias', value: performance.wins },
    { name: 'Derrotas', value: performance.losses },
    { name: 'Empates', value: performance.draws }
  ].filter(d => d.value > 0);

  const primaryStatKey = isSetBased ? 'setsWon' : (sportName === 'Basquetebol' ? 'points' : 'goals');
  const topPerformers = [...athleteStats].sort((a, b) => (b.averages[primaryStatKey] || 0) - (a.averages[primaryStatKey] || 0));

  return (
    <div className="report-wrapper">
      <div className="print-header">
        <div className="print-header-content">
          <img src="/CDP_logo.png" alt="CDP Logo" className="print-logo" />
          <div className="print-title-group">
            <h1>Relatório de Performance Desportiva</h1>
            <div className="print-subtitle">
              <span>{report.teamName}</span>
              <span className="print-divider">|</span>
              <span>{sportName}</span>
              <span className="print-divider">|</span>
              <span>Época {report.season}</span>
            </div>
          </div>
          <div className="print-date">
            Gerado em: {new Date().toLocaleDateString('pt-PT')}
          </div>
        </div>
      </div>

      <div className="report-container" id="report-content">
        <header className="report-header">
          <div className="header-top-bar">
            <button className="back-btn-premium" onClick={() => navigate(-1)}>
              <FaArrowLeft /> <span>Voltar</span>
            </button>
            <button className="export-btn-premium no-print" onClick={() => window.print()}>
              <FaPrint /> <span>Imprimir Relatório</span>
            </button>
          </div>

          <div className="header-main-content">
            <img src="/CDP_logo.png" alt="Club Logo" className="header-bg-logo" />
            <div className="title-layer">
              <h1>Relatório da Época</h1>
              <div className="meta-info">
                <span className="sport-tag">{sportName}</span>
                <span className="divider">•</span>
                <span className="team-name">{report.teamName}</span>
                <span className="divider">•</span>
                <span className="season-badge">{report.season}</span>
              </div>
            </div>
          </div>
        </header>

        <div className="report-grid">
          <section className="report-section hero-row">
            <div className="glass-card stat-hero">
              <div className="stat-hero-icon"><FaTrophy /></div>
              <div className="stat-hero-info">
                <span className="label">Percentagem de Vitória</span>
                <span className="value">{performance.winRate}%</span>
                <div className="progress-mini">
                  <div className="progress-fill" style={{ width: `${performance.winRate}%` }}></div>
                </div>
              </div>
            </div>

            <div className="glass-card stat-summary">
              <div className="summary-item">
                <span className="val">{performance.totalGames}</span>
                <span className="lbl">Jogos</span>
              </div>
              <div className="summary-divider"></div>
              <div className="summary-item">
                <span className="val success">{performance.wins}</span>
                <span className="lbl">Vitórias</span>
              </div>
              <div className="summary-divider"></div>
              <div className="summary-item">
                <span className="val danger">{performance.losses}</span>
                <span className="lbl">Derrotas</span>
              </div>
              {performance.draws > 0 && (
                <>
                  <div className="summary-divider"></div>
                  <div className="summary-item">
                    <span className="val warning">{performance.draws}</span>
                    <span className="lbl">Empates</span>
                  </div>
                </>
              )}
            </div>

            <div className="glass-card stat-scoring">
              <div className="scoring-row">
                <div className="scoring-item">
                  <span className="lbl">{scoreLabel} Marcados</span>
                  <span className="val">{performance.totalScored}</span>
                </div>
                <div className="scoring-item">
                  <span className="lbl">{scoreLabel} Sofridos</span>
                  <span className="val">{performance.totalConceded}</span>
                </div>
              </div>
              <div className="scoring-ratio">
                Média: {(performance.totalScored / (performance.totalGames || 1)).toFixed(1)} por jogo
              </div>
            </div>
          </section>

          <section className="report-section split-row">
            <div className="glass-card chart-card wide">
              <h3><FaChartLine /> Evolução da Assiduidade (%)</h3>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={attendance.monthlyAttendance}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#cbd5e1" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} domain={[0, 100]} />
                    <Tooltip
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="rate"
                      stroke="#1d4ed8"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#1d4ed8' }}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="glass-card chart-card">
              <h3>Distribuição de Resultados</h3>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="pie-legend">
                {pieData.map((d, i) => (
                  <div key={d.name} className="legend-item">
                    <span className="dot" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                    <span className="txt">{d.name}: {d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="report-section">
            <div className="glass-card stats-table-card" id="athletes-table-section">
              <div className="card-header-flex">
                <h3><FaChartLine /> Médias Estatísticas da Equipa (Por Jogo)</h3>
              </div>

              <div className="table-overflow">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')}>Atleta {getSortIcon('name')}</th>
                      <th onClick={() => handleSort('gamesPlayed')}>Jogos {getSortIcon('gamesPlayed')}</th>
                      {sportConfig?.stats?.filter(s => s.inputType === 'number').map(s => (
                        <th key={s.key} onClick={() => handleSort(s.key)}>
                          {s.label} {getSortIcon(s.key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAthleteStats.map(athlete => (
                      <tr key={athlete.athleteId}>
                        <td className="player-cell">
                          <span className="player-name">{athlete.name}</span>
                        </td>
                        <td className="center-cell">{athlete.gamesPlayed}</td>
                        {sportConfig?.stats?.filter(s => s.inputType === 'number').map(s => (
                          <td key={s.key} className="stat-cell">
                            {athlete.averages[s.key] !== undefined ? athlete.averages[s.key] : '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile View: Cards instead of table */}
              <div className="mobile-athlete-cards">
                {sortedAthleteStats.map(athlete => (
                  <div className="athlete-mobile-card" key={athlete.athleteId}>
                    <div className="athlete-card-header">
                      <span className="athlete-card-name">{athlete.name}</span>
                      <span className="athlete-card-games">{athlete.gamesPlayed} Jogos</span>
                    </div>
                    <div className="athlete-card-stats">
                      {sportConfig?.stats?.filter(s => s.inputType === 'number').map(s => (
                        <div className="card-stat-item" key={s.key}>
                          <span className="card-stat-label">{s.label}</span>
                          <span className="card-stat-value">
                            {athlete.averages[s.key] !== undefined ? athlete.averages[s.key] : '-'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="report-section split-row">
            <div className="glass-card list-card">
              <h3><FaUsers /> Atletas Destaque (Ranking)</h3>
              <div className="top-performers-list">
                {topPerformers.slice(0, 5).map((player, idx) => {
                  const mainStatValue = player.averages[primaryStatKey] || 0;

                  return (
                    <div className="performer-item" key={player.athleteId}>
                      <div className="performer-ranking">#{idx + 1}</div>
                      <div className="performer-info">
                        <h4>{player.name}</h4>
                        <p>{sportName === 'Basquetebol' ? 'Ponto Médio/Jogo' : 'Média de Golos'}</p>
                      </div>
                      <div className="performer-stat">
                        <span className="num">{mainStatValue}</span>
                        <span className="unit">{scoreLabel}/J</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="glass-card list-card">
              <h3><FaCalendarAlt /> Assiduidade Individual</h3>
              <div className="attendance-list">
                {attendance.athleteAttendance.slice(0, 8).map(athlete => (
                  <div className="attendance-row" key={athlete.athleteId}>
                    <span className="at-name">{athlete.name}</span>
                    <div className="at-progress-container">
                      <div className="at-progress-bar">
                        <div
                          className={`at-progress-fill ${athlete.rate >= 80 ? 'good' : athlete.rate >= 50 ? 'avg' : 'bad'}`}
                          style={{ width: `${athlete.rate}%` }}
                        ></div>
                      </div>
                      <span className="at-percent">{athlete.rate}%</span>
                    </div>
                    {athlete.rate < 50 && (
                      <span className="at-alert" title="Abaixo do esperado"><FaExclamationTriangle /></span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default SeasonReport;